import { useEffect } from 'react';
import { useTour } from '@reactour/tour';

// hooks
import type { User } from 'shared/hooks/api/user';
import useTourState from 'productTours/hooks/useTourState';

// types

interface TourHandlerProps {
  user: User;
}

const TourHandler = ({ user }: TourHandlerProps): null => {
  const { currentStep, steps, setCurrentStep, setIsOpen } = useTour();

  // Initialize tour state
  useTourState({ user });

  // Handle click capture for tour navigation
  useEffect(() => {
    // Function to advance to the next step
    const advanceToNextStep = () => {
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        setIsOpen(false);
      }
    };

    const handleClick = (event: MouseEvent) => {
      const step = steps[currentStep];
      if (!step) return;

      // Try to find if we clicked on or near the highlighted element
      if (step.selector) {
        try {
          const targetElement = document.querySelector(step.selector as string);
          const highlightElement = document.querySelector(
            '[data-tour-elem="highlight"]'
          );

          const clickedTarget =
            (targetElement && targetElement.contains(event.target as Node)) ||
            (highlightElement &&
              highlightElement.contains(event.target as Node));

          if (clickedTarget) {
            // Execute step action if defined
            if (step.action) {
              try {
                step.action(targetElement as Element);
                // Advance to the next step after a delay
                setTimeout(advanceToNextStep, 300);
              } catch (error) {
                console.error('Error executing step action:', error);
              }
            } else {
              // No action defined, advance to the next step
              setTimeout(advanceToNextStep, 300);
            }
          }
        } catch (error) {
          console.error('Error handling click:', error);
        }
      }
    };

    // Add global capture phase click listener
    document.addEventListener('click', handleClick, true);

    // Cleanup
    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [currentStep, steps, setCurrentStep, setIsOpen]);

  return null;
};

export default TourHandler;
